import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Mail, Trash2, Megaphone, Save, User2, ExternalLink, Clock } from "lucide-react";

const TYPE_LABEL = {
  material_request: "Materiale / Campioni",
  ambassador_apply: "Candidatura Ambassador",
  kit_request: "Kit Ambassador",
  sample_request: "Campionatura",
  contact_message: "Messaggio / Contatto",
};
const STATUS_OPTIONS = [
  { value: "nuovo", label: "Nuova" },
  { value: "in_valutazione", label: "In valutazione" },
  { value: "approvata", label: "Approvata" },
  { value: "rifiutata", label: "Rifiutata" },
];
const STATUS_CLS = {
  nuovo: "bg-brema-red/15 text-brema-red",
  in_valutazione: "bg-amber-500/15 text-amber-600 dark:text-amber-300",
  approvata: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-300",
  rifiutata: "bg-muted text-muted-foreground",
  letto: "bg-sky-500/15 text-sky-600 dark:text-sky-300",
};
const dmyhm = (iso) => { try { return new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return "—"; } };
const FOLLOWUP_DAYS = 3;
const daysSince = (iso) => { try { return (Date.now() - new Date(iso).getTime()) / 86400000; } catch { return 0; } };

// Rende cliccabile un profilo social (@handle → Instagram) o un sito/URL; altrimenti testo semplice.
const socialHref = (v) => {
  const s = (v || "").trim();
  if (!s) return null;
  if (/^https?:\/\//i.test(s)) return s;
  if (s.startsWith("@")) return `https://instagram.com/${s.replace(/^@+/, "")}`;
  if (/^www\./i.test(s)) return `https://${s}`;
  if (/^[\w.-]+\.[a-z]{2,}([/?].*)?$/i.test(s)) return `https://${s}`;
  return null;
};
function Linkified({ value, testid }) {
  const href = socialHref(value);
  if (!href) return <span>{value}</span>;
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-brand underline decoration-dotted underline-offset-2 hover:text-brand/80" data-testid={testid}>
      {value} <ExternalLink className="h-3 w-3 shrink-0" />
    </a>
  );
}

// Tabella richieste/proposte con workflow (Nuova → In valutazione → Approvata/Rifiutata) + nota interna.
export function LoungeRequestsAdminTab({ page, onCount }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState([]);
  const [notes, setNotes] = useState({});

  const load = () => {
    setLoading(true);
    api.get(`/lounge-requests?page=${page}`).then(({ data }) => {
      setRows(data.requests || []);
      setNotes(Object.fromEntries((data.requests || []).map((r) => [r.id, r.internal_note || ""])));
      onCount && onCount(data.unread || 0);
    }).catch(() => setRows([])).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [page]);

  const setStatus = async (id, status) => {
    try { await api.patch(`/lounge-requests/${id}`, { status }); load(); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };
  const saveNote = async (id) => {
    try { await api.patch(`/lounge-requests/${id}`, { internal_note: notes[id] || "" }); toast.success("Nota salvata"); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };
  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const deleteSelected = async () => {
    if (selected.length === 0) return;
    try {
      const { data } = await api.post("/lounge-requests/delete-selected", { ids: selected });
      toast.success(`Eliminate ${data.deleted} richieste`); setSelected([]); load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  if (loading) return <div className="flex items-center gap-2 py-10 text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Caricamento…</div>;

  return (
    <div className="space-y-4" data-testid="lounge-requests-admin">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="text-sm text-muted-foreground">Proposte, richieste e messaggi «Contattaci» ricevuti dalla pagina pubblica. Ogni invio genera anche una notifica e-mail al destinatario Marketing (Impostazioni).</div>
        {selected.length > 0 && (
          <Button variant="danger" size="sm" onClick={deleteSelected} data-testid="lounge-req-delete-selected">
            <Trash2 className="mr-1.5 h-4 w-4" /> Elimina selezionate ({selected.length})
          </Button>
        )}
      </div>

      {rows.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border py-14 text-center text-muted-foreground" data-testid="lounge-req-empty">
          <Megaphone className="mx-auto mb-2 h-8 w-8 opacity-40" /> Nessuna richiesta ricevuta.
        </div>
      ) : (
        <div className="space-y-3">
          {rows.map((r) => (
            <div key={r.id} className="rounded-xl border border-border bg-brema-card p-4" data-testid={`lounge-req-${r.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="flex items-start gap-3">
                  <Checkbox checked={selected.includes(r.id)} onCheckedChange={() => toggle(r.id)} data-testid={`lounge-req-check-${r.id}`} />
                  <div>
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="rounded bg-brema-red/15 px-2 py-0.5 text-xs font-semibold text-brema-red">{TYPE_LABEL[r.type] || r.type}</span>
                      <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${STATUS_CLS[r.status] || STATUS_CLS.letto}`} data-testid={`lounge-req-status-badge-${r.id}`}>
                        {(STATUS_OPTIONS.find((o) => o.value === r.status) || {}).label || r.status}
                      </span>
                      {r.type === "ambassador_apply" && r.status === "nuovo" && daysSince(r.created_at) >= FOLLOWUP_DAYS && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-amber-500/15 px-2 py-0.5 text-[10px] font-bold uppercase text-amber-600 dark:text-amber-300" data-testid={`lounge-req-followup-${r.id}`} title={`In attesa di risposta da oltre ${FOLLOWUP_DAYS} giorni`}>
                          <Clock className="h-3 w-3" /> Da ricontattare
                        </span>
                      )}
                      <span className="text-xs text-muted-foreground">{dmyhm(r.created_at)}</span>
                    </div>
                    <div className="mt-1.5 text-base font-semibold">{r.name}</div>
                    <div className="text-sm text-muted-foreground">{r.email}</div>
                    {(r.social || r.organization) && (
                      <div className="mt-1 flex items-center gap-1.5 text-sm font-medium" data-testid={`lounge-req-partner-${r.id}`}>
                        <span className="rounded bg-brand/15 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-brand">{r.social ? "Social" : "Testata"}</span>
                        <Linkified value={r.social || r.organization} testid={`lounge-req-partner-link-${r.id}`} />
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <Select value={r.status} onValueChange={(v) => setStatus(r.id, v)}>
                    <SelectTrigger className="h-9 w-[170px]" data-testid={`lounge-req-status-${r.id}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {STATUS_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <a href={`mailto:${r.email}`}><Button variant="outline" size="sm"><Mail className="mr-1.5 h-4 w-4" /> Rispondi</Button></a>
                </div>
              </div>

              <div className="mt-3 grid grid-cols-1 gap-x-6 gap-y-1 pl-8 text-sm sm:grid-cols-2">
                {(r.ref_recipient && r.ref_recipient.name) && (
                  <div className="sm:col-span-2 flex items-center gap-1.5 font-medium text-foreground" data-testid={`lounge-req-recipient-${r.id}`}>
                    <User2 className="h-4 w-4 text-brand" /> Da: {r.ref_recipient.name} <span className="text-xs uppercase text-muted-foreground">({r.ref_recipient.type})</span>
                  </div>
                )}
                {r.subject && <div className="sm:col-span-2" data-testid={`lounge-req-subject-${r.id}`}><span className="text-muted-foreground">Oggetto:</span> <span className="font-medium text-foreground">{r.subject}</span></div>}
                {r.social && r.organization && <div><span className="text-muted-foreground">Testata/Org.:</span> <Linkified value={r.organization} testid={`lounge-req-org-link-${r.id}`} /></div>}
                {r.discipline && <div><span className="text-muted-foreground">Disciplina:</span> {r.discipline}</div>}
                {r.phone && <div><span className="text-muted-foreground">Telefono:</span> {r.phone}</div>}
                {r.address && <div><span className="text-muted-foreground">Indirizzo:</span> {r.address}</div>}
                {r.items && <div className="sm:col-span-2"><span className="text-muted-foreground">Materiale:</span> {r.items}</div>}
                {r.ref && <div><span className="text-muted-foreground">Ref:</span> <span className="font-mono text-xs">{r.ref}</span></div>}
                {r.message && <div className="sm:col-span-2 whitespace-pre-wrap"><span className="text-muted-foreground">Messaggio:</span> {r.message}</div>}
              </div>

              {Array.isArray(r.samples) && r.samples.length > 0 && (
                <div className="mt-3 pl-8" data-testid={`lounge-req-samples-${r.id}`}>
                  <div className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Capi selezionati</div>
                  <ul className="flex flex-wrap gap-2">
                    {r.samples.map((s, i) => (
                      <li key={i} className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-brema-card px-2.5 py-1 text-sm">
                        <span className="font-medium text-foreground">{s.name}</span>
                        {s.size && <span className="text-xs text-muted-foreground">· {s.size}</span>}
                        <span className="rounded bg-brand/15 px-1.5 text-xs font-bold text-brand">×{s.qty}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="mt-3 pl-8">
                <div className="flex items-end gap-2">
                  <div className="flex-1">
                    <label className="mb-1 block text-xs text-muted-foreground">Nota interna</label>
                    <Textarea rows={2} value={notes[r.id] || ""} onChange={(e) => setNotes((n) => ({ ...n, [r.id]: e.target.value }))} data-testid={`lounge-req-note-${r.id}`} />
                  </div>
                  <Button variant="outline" size="sm" onClick={() => saveNote(r.id)} data-testid={`lounge-req-note-save-${r.id}`}><Save className="mr-1.5 h-4 w-4" /> Salva nota</Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
